/**
 * Admin endpoint surface the console calls (docs/modules/console.md "Needs from others"). Request/response
 * shapes come from @windrop/contracts wherever one exists; the few console-specific DTOs (dashboard, login
 * continuation, config versions, passkey options) are defined here until contracts carry them.
 */
import type {
  AssetDto,
  ConfigVersionDto as ContractsConfigVersionDto,
  FinalizeAssetRequest,
  LotteryMetricsDto,
  LotteryPlatformsDto,
  PresignAssetRequest,
  PresignAssetResponse,
  PutLotteryPlatformsRequest,
  ShareLinksDto,
  SimulationReportFullDto,
  ValidateLotteryResponse,
} from '@windrop/contracts';
import {
  IsoDateTime,
  LotteryConfigSchema,
  MoneyDto,
  SocialPlatform,
  StaffSessionDto,
  Ulid,
  type AuditPageDto,
  type AuditQuery,
  type CancelLotteryRequest,
  type CreateLotteryRequest,
  type DrawPrecheckDto,
  type LifecycleTransitionResponse,
  type LotteryAdminDto,
  type LotteryListQuery,
  type LotteryListRowDto,
  type SimulateLotteryRequest,
  type SimulationReportDto,
  type StaffLoginRequest,
  type StaffLoginResponse,
  type StepUpRequest,
  type UnmaskRequest,
  type UnmaskResponse,
  type UpdateLotteryRequest,
  type VerifyLoginRequest,
  type WinnerLookupDto,
} from '@windrop/contracts';
import { z } from 'zod';
import { api, request, setCsrfToken, type QueryValue } from './client';
import type {
  RecordWinnerHandleRequest,
  SupportCaseAdminDetailDto,
  SupportCaseListQuery,
  SupportCasePageDto,
  UpdateSupportCaseRequest,
  UpdateWinnerContactRequest,
  WinnerBoardPageDto,
  WinnerBoardQuery,
  WinnerExportDto,
  WinnerLookupResultDto,
  WinnerRowDto,
} from '@windrop/contracts';

// ---------------------------------------------------------------------------------------------------------
// Console-local DTOs (to be promoted into @windrop/contracts by the owning agents)
// ---------------------------------------------------------------------------------------------------------

/** `POST /v1/admin/auth/verify`: either a session or the next factor the staff member must present. */
export const VerifyLoginResponse = z.discriminatedUnion('next', [
  z.object({ next: z.literal('DONE'), session: StaffSessionDto }),
  z.object({ next: z.literal('TOTP'), challengeId: Ulid }),
  z.object({ next: z.literal('PASSKEY'), challengeId: Ulid }),
  /** MFA_ENROLLMENT_REQUIRED: the pending-MFA session exists; enrol an authenticator at /login/enroll (D-2205). */
  z.object({ next: z.literal('ENROLL'), challengeId: Ulid }),
]);
export type VerifyLoginResponse = z.infer<typeof VerifyLoginResponse>;

/** `POST /v1/admin/auth/totp/enroll` (identity `TotpEnrollmentDto`): secret shown once as QR + manual key. */
export const TotpEnrollmentDto = z.object({
  otpauthUri: z.string(),
  qrDataUrl: z.string(),
  manualKey: z.string(),
  issuer: z.string(),
  accountLabel: z.string(),
  expiresAt: z.string(),
});
export type TotpEnrollmentDto = z.infer<typeof TotpEnrollmentDto>;

/** WebAuthn request options as produced by `@simplewebauthn/server` (`generateAuthenticationOptions`). */
export const PasskeyOptionsResponse = z.object({
  challengeId: Ulid,
  options: z.record(z.string(), z.unknown()),
});
export type PasskeyOptionsResponse = z.infer<typeof PasskeyOptionsResponse>;

export const ConfigVersionDto = z.object({
  configVersion: z.number().int().min(1),
  config: LotteryConfigSchema,
  createdAt: IsoDateTime,
  createdBy: z.object({ staffId: Ulid, name: z.string() }).nullable(),
  changeNote: z.string().nullable(),
  participantRemedy: z.string().nullable(),
  published: z.boolean(),
});
export type ConfigVersionDto = z.infer<typeof ConfigVersionDto>;

/** `GET /v1/admin/dashboard` (§16.2 Dashboard). Every tile is nullable: null = "no data yet", never zero. */
export const DashboardDto = z.object({
  asOf: IsoDateTime,
  lotteryId: Ulid.nullable(),
  funnel: z
    .object({
      views: z.number().int(),
      entryStarts: z.number().int(),
      confirmedEntries: z.number().int(),
      paymentPending: z.number().int(),
    })
    .nullable(),
  entriesBySource: z.array(
    z.object({ platform: SocialPlatform.or(z.literal('DIRECT')), count: z.number().int() }),
  ),
  paidReconciliation: z
    .object({
      succeeded: z.number().int(),
      pending: z.number().int(),
      exceptions: z.number().int(),
      collected: MoneyDto,
    })
    .nullable(),
  prizeLiability: z
    .object({ committed: MoneyDto, cap: MoneyDto.nullable(), unitsReserved: z.number().int() })
    .nullable(),
  claimSla: z
    .object({
      open: z.number().int(),
      overdue: z.number().int(),
      medianHoursToDecision: z.number().nullable(),
    })
    .nullable(),
  fulfillmentSla: z
    .object({ open: z.number().int(), exceptions: z.number().int(), overdue: z.number().int() })
    .nullable(),
  riskExceptions: z.object({ open: z.number().int(), highSeverity: z.number().int() }).nullable(),
  /** Hourly confirmed entries for the trend chart; empty when nothing happened yet. */
  entriesSeries: z.array(z.object({ at: IsoDateTime, confirmedEntries: z.number().int() })),
});
export type DashboardDto = z.infer<typeof DashboardDto>;

export interface ListResponse<T> {
  items: T[];
}

// ---------------------------------------------------------------------------------------------------------
// Identity / auth (§17.1, D-003)
// ---------------------------------------------------------------------------------------------------------

/**
 * What the API's login/verify/passkey/step-up routes answer (identity module `LoginStepResponse`, D-703): a
 * status plus the session when one exists. The console pages work on the `next` continuation below, so the
 * mapping lives here (D-1001 thin client; swap for `@windrop/api-client` later).
 */
interface LoginStepResponse {
  status: 'OK' | 'MFA_REQUIRED' | 'MFA_ENROLLMENT_REQUIRED';
  methods: Array<'TOTP' | 'PASSKEY'>;
  /** Pending-MFA steps nest the session under `me` (or null); completed logins answer the session flat. */
  me?: (StaffSessionDto & { csrf?: string }) | null;
  staffId?: string;
  /** Double-submit CSRF token for this session (D-702); the console cannot read the API-host cookie. */
  csrf?: string;
}

const sessionOf = (r: LoginStepResponse): StaffSessionDto | null => {
  setCsrfToken(r.csrf ?? r.me?.csrf);
  return r.me ?? (r.staffId ? (r as unknown as StaffSessionDto) : null);
};

const toVerifyResponse = (challengeId: string, r: LoginStepResponse): VerifyLoginResponse => {
  const session = sessionOf(r);
  if (r.status === 'OK' && session) return { next: 'DONE', session };
  // MFA_ENROLLMENT_REQUIRED: the API holds a pending-MFA session (cookie) and no factor is enrolled yet; the
  // enrolment screen creates the TOTP secret on that session and confirms it (D-2205).
  if (r.status === 'MFA_ENROLLMENT_REQUIRED' || r.methods.length === 0)
    return { next: 'ENROLL', challengeId };
  return { next: r.methods.includes('TOTP') ? 'TOTP' : 'PASSKEY', challengeId };
};

export const identityApi = {
  me: async (): Promise<StaffSessionDto> => {
    const s = await api.get<StaffSessionDto & { csrf?: string }>('/v1/admin/me');
    setCsrfToken(s.csrf);
    return s;
  },
  login: (body: StaffLoginRequest) =>
    api.post<StaffLoginResponse>('/v1/admin/auth/magic-link', body, { idempotencyKey: false }),
  verify: async (body: VerifyLoginRequest): Promise<VerifyLoginResponse> => {
    // The magic-link code is single-use: the first verify establishes a pending-MFA session (cookie), and the
    // authenticator code then completes login through /totp/confirm on that session (D-703).
    const step = body.totp
      ? await api.post<LoginStepResponse>(
          '/v1/admin/auth/totp/confirm',
          { code: body.totp },
          { idempotencyKey: false },
        )
      : await api.post<LoginStepResponse>(
          '/v1/admin/auth/verify',
          { challengeId: body.challengeId, code: body.code },
          { idempotencyKey: false },
        );
    return toVerifyResponse(body.challengeId, step);
  },
  /** Creates the TOTP secret on the pending-MFA session; never logged, shown once (§17.1). */
  totpEnroll: () =>
    api.post<TotpEnrollmentDto>('/v1/admin/auth/totp/enroll', undefined, { idempotencyKey: false }),
  /** Confirms the first authenticator code: completes the pending login (flat session, `status: 'OK'`). */
  totpConfirm: async (challengeId: string, code: string): Promise<VerifyLoginResponse> =>
    toVerifyResponse(
      challengeId,
      await api.post<LoginStepResponse>('/v1/admin/auth/totp/confirm', { code }, { idempotencyKey: false }),
    ),
  passkeyOptions: (body: { email?: string; challengeId?: string }) =>
    api.post<PasskeyOptionsResponse>(
      '/v1/admin/auth/passkey/assert/options',
      body.email ? { email: body.email } : {},
      { idempotencyKey: false },
    ),
  passkeyVerify: async (body: { challengeId: string; assertion: unknown }): Promise<VerifyLoginResponse> =>
    toVerifyResponse(
      body.challengeId,
      await api.post<LoginStepResponse>(
        '/v1/admin/auth/passkey/assert',
        { challengeId: body.challengeId, credential: body.assertion },
        { idempotencyKey: false },
      ),
    ),
  stepUp: async (body: StepUpRequest): Promise<StaffSessionDto> => {
    const r = await api.post<LoginStepResponse>('/v1/admin/auth/step-up', body, { idempotencyKey: false });
    const session = sessionOf(r);
    if (!session) throw new Error('Step-up did not return a session');
    return session;
  },
  stepUpPasskeyOptions: () =>
    api.post<PasskeyOptionsResponse>('/v1/admin/auth/passkey/assert/options', {}, { idempotencyKey: false }),
  logout: () => api.post<void>('/v1/admin/auth/logout', {}, { idempotencyKey: false }),
  unmask: (body: UnmaskRequest) => api.post<UnmaskResponse>('/v1/admin/identity/unmask', body),
};

// ---------------------------------------------------------------------------------------------------------
// Lotteries (§15.2 admin surface, Appendix A lifecycle)
// ---------------------------------------------------------------------------------------------------------

export type LifecycleAction =
  'submit-review' | 'approve' | 'publish' | 'pause' | 'resume' | 'close' | 'cancel';

export const lotteriesApi = {
  list: (query: LotteryListQuery = {}) =>
    api.get<ListResponse<LotteryListRowDto>>('/v1/admin/lotteries', query),
  get: (lotteryId: string) => api.get<LotteryAdminDto>(`/v1/admin/lotteries/${lotteryId}`),
  create: (body: CreateLotteryRequest) => api.post<LotteryAdminDto>('/v1/admin/lotteries', body),
  update: (lotteryId: string, body: UpdateLotteryRequest) =>
    api.patch<LotteryAdminDto>(`/v1/admin/lotteries/${lotteryId}`, body),
  versions: (lotteryId: string) =>
    api.get<ListResponse<ConfigVersionDto>>(`/v1/admin/lotteries/${lotteryId}/versions`),
  simulate: (lotteryId: string, body: Partial<SimulateLotteryRequest> = {}) =>
    api.post<SimulationReportDto>(`/v1/admin/lotteries/${lotteryId}/simulate`, body),
  precheck: (lotteryId: string) => api.get<DrawPrecheckDto>(`/v1/admin/lotteries/${lotteryId}/draw/precheck`),
  transition: (lotteryId: string, action: LifecycleAction, body: Record<string, unknown> = {}) =>
    api.post<LifecycleTransitionResponse>(`/v1/admin/lotteries/${lotteryId}/${action}`, body),
  cancel: (lotteryId: string, body: CancelLotteryRequest) =>
    api.post<LifecycleTransitionResponse>(`/v1/admin/lotteries/${lotteryId}/cancel`, body),
};

// ---------------------------------------------------------------------------------------------------------
// Cross-cutting reads
// ---------------------------------------------------------------------------------------------------------

export const dashboardApi = {
  get: (lotteryId?: string) => api.get<DashboardDto>('/v1/admin/dashboard', { lotteryId }),
};

export const auditApi = {
  list: (query: AuditQuery & { page?: number; pageSize?: number }) =>
    api.get<AuditPageDto>('/v1/admin/audit', query),
};

export const winnersApi = {
  lookup: (winId: string) =>
    api.get<WinnerLookupDto>(`/v1/admin/winners/lookup/${encodeURIComponent(winId)}`),
};

// ---------------------------------------------------------------------------------------------------------
// Winners board + support (winners agent, §12.3, §11.4) — appended; `winnersApi.lookup` above stays as is.
// ---------------------------------------------------------------------------------------------------------

export const winnersBoardApi = {
  board: (query: Partial<WinnerBoardQuery> & { cursor?: string; limit?: number } = {}) =>
    api.get<WinnerBoardPageDto>('/v1/admin/winners', query as Record<string, QueryValue>),
  lookup: (winId: string, link: { exp?: number; sig?: string } = {}) =>
    api.get<WinnerLookupResultDto>(`/v1/admin/winners/lookup/${encodeURIComponent(winId)}`, link),
  updateContact: (ref: string, body: UpdateWinnerContactRequest) =>
    api.patch<WinnerRowDto>(`/v1/admin/winners/${encodeURIComponent(ref)}/contact`, body),
  recordHandle: (ref: string, body: RecordWinnerHandleRequest) =>
    api.post<WinnerRowDto>(`/v1/admin/winners/${encodeURIComponent(ref)}/handle`, body),
  exportPublicity: (query: { lotteryId?: string; purpose?: string } = {}) =>
    api.get<WinnerExportDto>('/v1/admin/winners/export', { ...query, format: 'JSON' }),
};

export const supportApi = {
  list: (query: Partial<SupportCaseListQuery> & { cursor?: string; limit?: number } = {}) =>
    api.get<SupportCasePageDto>('/v1/admin/support/cases', query as Record<string, QueryValue>),
  get: (id: string) =>
    api.get<SupportCaseAdminDetailDto>(`/v1/admin/support/cases/${encodeURIComponent(id)}`),
  update: (id: string, body: UpdateSupportCaseRequest) =>
    api.patch<SupportCaseAdminDetailDto>(`/v1/admin/support/cases/${encodeURIComponent(id)}`, body),
};

// ---------------------------------------------------------------------------------------------------------
// Lottery configuration operations (lotteries agent, §5.4 gates, §5.5, §12.1, §16.1 step 8) — appended;
// `lotteriesApi` above stays as is. DTOs come from @windrop/contracts (`lotteries.ts` extensions).
// ---------------------------------------------------------------------------------------------------------

export const lotteriesOpsApi = {
  validate: (lotteryId: string, version?: number) =>
    api.post<ValidateLotteryResponse>(
      `/v1/admin/lotteries/${lotteryId}/validate${version ? `?version=${version}` : ''}`,
      {},
      { idempotencyKey: false },
    ),
  simulateFull: (lotteryId: string, body: Partial<SimulateLotteryRequest> = {}) =>
    api.post<SimulationReportFullDto>(`/v1/admin/lotteries/${lotteryId}/simulate`, body, {
      idempotencyKey: false,
    }),
  versions: (lotteryId: string) =>
    api.get<ListResponse<ContractsConfigVersionDto>>(`/v1/admin/lotteries/${lotteryId}/versions`),
  approveVersion: (lotteryId: string, version: number, note?: string) =>
    api.post<ContractsConfigVersionDto>(
      `/v1/admin/lotteries/${lotteryId}/versions/${version}/approve`,
      note ? { note } : {},
    ),
  publishVersion: (lotteryId: string, version: number) =>
    api.post<ContractsConfigVersionDto>(`/v1/admin/lotteries/${lotteryId}/versions/${version}/publish`, {}),
  shareLinks: (lotteryId: string) => api.get<ShareLinksDto>(`/v1/admin/lotteries/${lotteryId}/share-links`),
  platforms: (lotteryId: string) =>
    api.get<LotteryPlatformsDto>(`/v1/admin/lotteries/${lotteryId}/platforms`),
  putPlatforms: (lotteryId: string, body: PutLotteryPlatformsRequest) =>
    request<LotteryPlatformsDto>(`/v1/admin/lotteries/${lotteryId}/platforms`, { method: 'PUT', body }),
  metrics: (lotteryId: string) => api.get<LotteryMetricsDto>(`/v1/admin/lotteries/${lotteryId}/metrics`),
  /** Catalogue asset upload (prizes agent): presign → PUT → finalize; used by the Experience step hero media. */
  presignAsset: (body: PresignAssetRequest) =>
    api.post<PresignAssetResponse>('/v1/admin/assets/presign', body),
  finalizeAsset: (assetId: string, body: FinalizeAssetRequest) =>
    api.post<AssetDto>(`/v1/admin/assets/${assetId}/finalize`, body),
};
